import styled, { keyframes, css } from 'styled-components';
import { Typography } from 'antd';

const { Title } = Typography;

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const cardEntrance = css`
  opacity: 0;
  animation: ${fadeInUp} 0.4s ease-out forwards;
  animation-delay: var(--entrance-delay, 0s);
`;

const slideInRight = keyframes`
  from {
    opacity: 0;
    transform: translateX(40px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const slideInLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-40px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

export const FullPageContainer = styled.div`
  min-height: 100vh;
  background: #f5f5f5;
`;

export const PageContainer = styled.div`
  min-height: 100vh;
  background: #f5f5f5;
  margin: 0 auto;
  padding: 0 24px;
  
  @media (max-width: 768px) {
    padding: 0 16px;
  }
  
  @media (max-width: 480px) {
    padding: 0 12px;
  }
`;

export const ContentContainer = styled.div`
  max-width: 1200px;
  margin-left: auto;
  margin-right: auto;
  
  @media (max-width: 768px) {
    padding: 0 8px;
  }
  
  @media (max-width: 480px) {
    padding: 0;
  }
`;

export const WelcomeSection = styled.div`
  background: #f5f5f5;
  padding: 60px 24px;
  margin-bottom: 40px;
  
  @media (max-width: 768px) {
    padding: 40px 16px;
    margin-bottom: 24px;
  }
  
  @media (max-width: 480px) {
    padding: 32px 12px;
    margin-bottom: 16px;
  }
  
  img {
    @media (max-width: 480px) {
      height: 48px !important;
    }
  }
  
  h2 {
    @media (max-width: 768px) {
      font-size: 24px !important;
    }
    
    @media (max-width: 480px) {
      font-size: 20px !important;
    }
  }
  
  .ant-typography {
    @media (max-width: 480px) {
      font-size: 14px !important;
    }
  }
`;

export const EventTypeGrid = styled.div`
  margin-bottom: 40px;
  max-width: 1200px;
  margin-left: auto;
  margin-right: auto;
  
  @media (max-width: 768px) {
    margin-bottom: 24px;
  }
  
  @media (max-width: 480px) {
    margin-bottom: 16px;
  }
`;

export const EventTypeRow = styled.div`
  display: flex;
  align-items: center;
  padding: 24px;
  margin-bottom: 16px;
  background: white;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  cursor: pointer;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  ${cardEntrance}
  
  &:hover {
    border-color: #1890ff;
    box-shadow: 0 4px 16px rgba(24, 144, 255, 0.15);
  }
  
  &:last-child {
    margin-bottom: 0;
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    padding: 20px;
    gap: 16px;
  }
  
  @media (max-width: 480px) {
    padding: 16px;
    gap: 12px;
    margin-bottom: 12px;
  }
`;

export const EventTypeInfo = styled.div`
  flex: 1;
  margin: 0 24px;
  
  h4 {
    margin-bottom: 8px !important;
  }
  
  @media (max-width: 768px) {
    margin: 0;
    width: 100%;
  }
`;

export const EventTypeIcon = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  flex-shrink: 0;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

export const EventTypeTitle = styled(Title)`
  margin: 16px 0 8px 0 !important;
  color: #1a1a1a !important;
  font-weight: 600 !important;
  
  @media (max-width: 768px) {
    margin: 0 0 8px 0 !important;
    font-size: 18px !important;
  }
  
  @media (max-width: 480px) {
    font-size: 16px !important;
  }
`;

export const EventTypeDescription = styled.p`
  color: #666;
  margin-bottom: 8px;
  font-size: 14px;
  line-height: 1.5;
  
  @media (max-width: 480px) {
    font-size: 13px;
    margin-bottom: 6px;
  }
`;

export const EventTypeMeta = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  
  .meta-item {
    display: flex;
    align-items: center;
    gap: 6px;
    color: #666;
    font-size: 13px;
    
    .anticon {
      color: #1890ff;
    }
  }
  
  @media (max-width: 480px) {
    gap: 12px;
    
    .meta-item {
      font-size: 12px;
    }
  }
`;

export const EventTypeButton = styled.button`
  height: 40px;
  padding: 0 24px;
  border: none;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
  white-space: nowrap;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
  
  @media (max-width: 768px) {
    width: 100%;
    height: 44px;
    font-size: 15px;
  }
  
  @media (max-width: 480px) {
    height: 42px;
    font-size: 14px;
  }
`;

export const NoEventsContainer = styled.div`
  text-align: center;
  padding: 80px 24px;
  background: #fafafa;
  border-radius: 12px;
  margin: 40px 0;
  
  @media (max-width: 768px) {
    padding: 60px 20px;
    margin: 24px 0;
  }
  
  @media (max-width: 480px) {
    padding: 48px 16px;
    margin: 16px 0;
    border-radius: 8px;
  }
`;

export const NoEventsIcon = styled.div`
  font-size: 64px;
  color: #d9d9d9;
  margin-bottom: 24px;
  
  @media (max-width: 480px) {
    font-size: 48px;
    margin-bottom: 16px;
  }
`;

export const NoEventsTitle = styled(Title)`
  color: #595959 !important;
  margin-bottom: 16px !important;
  
  @media (max-width: 480px) {
    font-size: 20px !important;
    margin-bottom: 12px !important;
  }
`;

export const NoEventsMessage = styled.p`
  color: #8c8c8c;
  font-size: 16px;
  line-height: 1.6;
  margin-bottom: 0;
  max-width: 400px;
  margin-left: auto;
  margin-right: auto;
  
  @media (max-width: 480px) {
    font-size: 14px;
    max-width: 100%;
    padding: 0 8px;
  }
`;

export const RefreshButton = styled.button`
  margin-top: 24px;
  padding: 8px 16px;
  background-color: #1890FF;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s ease;
  box-shadow: none !important;
  
  &:hover {
    background-color: #40A9FF;
    transform: translateY(-1px);
  }
  
  &:active {
    background-color: #096DD9;
    transform: translateY(0);
  }
  
  &:focus {
    outline: none;
    box-shadow: none !important;
  }
`;

// Group card on the public booking page
export const GroupCard = styled.div`
  display: flex;
  align-items: center;
  padding: 24px;
  margin-bottom: 16px;
  background: white;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  cursor: pointer;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  ${cardEntrance}

  &:hover {
    border-color: #1890ff;
    box-shadow: 0 4px 16px rgba(24, 144, 255, 0.15);
  }

  &:last-child {
    margin-bottom: 0;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    padding: 20px;
    gap: 16px;
  }

  @media (max-width: 480px) {
    padding: 16px;
    gap: 12px;
    margin-bottom: 12px;
  }
`;

export const GroupEventCount = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #666;

  .anticon {
    color: #1890ff;
  }

  @media (max-width: 480px) {
    font-size: 12px;
  }
`;

export const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: transparent;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  color: #666;
  transition: all 0.2s ease;
  margin-bottom: 24px;

  &:hover {
    border-color: #1890ff;
    color: #1890ff;
  }

  @media (max-width: 480px) {
    padding: 6px 12px;
    font-size: 13px;
    margin-bottom: 16px;
  }
`;

export const SlideInView = styled.div`
  animation: ${props => props.$direction === 'left' ? slideInLeft : slideInRight} 0.35s ease-out;
`;

